import { RecBadge } from "@/components/common";
import { titleCase } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Link2, FileText, ChevronRight } from "lucide-react";

const LEVEL_N = { low: 1, medium: 2, high: 3 };
const LEVEL_COLOR = {
  high: "bg-[hsl(var(--success))]",
  medium: "bg-[hsl(var(--primary))]",
  low: "bg-[hsl(var(--warning))]",
};
const REC_ACCENT = {
  contact_now: "var(--success)",
  research_first: "var(--primary)",
  nurture: "var(--warning)",
  not_suitable: "var(--danger)",
};

function Meter({ label, level }) {
  const n = LEVEL_N[level] || 0;
  return (
    <div className="min-w-0">
      <div className="flex items-center justify-between gap-1">
        <span className="text-[11px] text-muted-foreground truncate">{label}</span>
        <span className="text-[11px] font-medium capitalize shrink-0">{level ? level : "—"}</span>
      </div>
      <div className="flex gap-1 mt-1" aria-label={`${label}: ${level || "unknown"}`}>
        {[1, 2, 3].map((i) => (
          <span key={i} className={`h-1.5 flex-1 rounded-full ${i <= n ? (LEVEL_COLOR[level] || "bg-muted") : "bg-muted"}`} />
        ))}
      </div>
    </div>
  );
}

export function CandidateCard({ c, selected, onOpen, onQuick, canTriage, busy }) {
  const a = c.assessment || {};
  const accent = REC_ACCENT[a.recommendation] || "var(--border)";
  const actionable = canTriage && c.status === "new";
  const act = (e, action) => { e.stopPropagation(); onQuick(c, action); };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(c.id)}
      onKeyDown={(e) => { if (e.key === "Enter") onOpen(c.id); }}
      data-selected={selected}
      data-testid={`candidate-card-${c.id}`}
      className={`group relative panel p-3.5 text-left cursor-pointer transition-shadow hover:shadow-md focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary)/0.4)] outline-none border-l-[3px] ${c.status !== "new" ? "opacity-80" : ""}`}
      style={{ borderLeftColor: `hsl(${accent})` }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="font-heading font-semibold text-[15px] leading-tight truncate" title={c.company_name}>{c.company_name}</h3>
          {c.location && <p className="meta truncate mt-0.5">{c.location}</p>}
        </div>
        <RecBadge value={a.recommendation} testId={`candidate-rec-${c.id}`} />
      </div>

      <p className="text-sm text-muted-foreground mt-2 line-clamp-2 min-h-[2.5rem]">{c.summary || "No summary available."}</p>

      <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 mt-3">
        <Meter label="Fit" level={a.fit} />
        <Meter label="Timing" level={a.timing} />
        <Meter label="Relationship" level={a.relationship} />
        <Meter label="Evidence" level={a.evidence_quality} />
      </div>

      <div className="flex items-center justify-between gap-2 mt-3 pt-2.5 border-t border-border">
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground min-w-0">
          <span className="inline-flex items-center gap-1" title="Sources"><Link2 className="h-3.5 w-3.5" />{c.sources?.length || 0}</span>
          <span className="inline-flex items-center gap-1" title="Evidence items"><FileText className="h-3.5 w-3.5" />{c.evidence?.length || 0}</span>
          {c.status !== "new" && <span className="chip chip-muted">{titleCase(c.status)}</span>}
        </div>
        {actionable ? (
          <div className="flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 transition-opacity">
            <Button size="icon" variant="ghost" title="Accept into Accounts" className="h-7 w-7 text-[hsl(var(--success))] hover:bg-[hsl(var(--success)/0.1)]" disabled={busy} onClick={(e) => act(e, "accept")} data-testid={`candidate-quick-accept-${c.id}`}><CheckCircle2 className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" title="Reject" className="h-7 w-7 text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger)/0.1)]" disabled={busy} onClick={(e) => act(e, "reject")} data-testid={`candidate-quick-reject-${c.id}`}><XCircle className="h-4 w-4" /></Button>
          </div>
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground/50 group-hover:text-muted-foreground" />
        )}
      </div>
    </div>
  );
}
